import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/components/SiteFooter";
import { Hero } from "@/components/Hero";
import { Reveal } from "@/components/Reveal";
import { HeroProductChips } from "@/components/HeroProductChips";
import { HomeChat } from "@/components/HomeChat";
import { categories } from "@/lib/products";
import { productArt } from "@/components/ProductArt";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      {
        title:
          "Tarifvergleiche.com — Preisvergleich für Elektronik, Handys & Computer",
      },
      {
        name: "description",
        content:
          "Vergleiche Preise für Elektronik, Smartphones und Computer bei mehreren Händlern und finde garantiert das beste Angebot.",
      },
      { property: "og:title", content: "Tarifvergleiche.com — Preisvergleich" },
      {
        property: "og:description",
        content:
          "Elektronik, Handys und Computer im Preisvergleich — transparent und aktuell.",
      },
    ],
  }),
  component: HomePage,
});

const stats = [
  { value: "12.000+", label: "Produkte im Vergleich" },
  { value: "80+", label: "Händler" },
  { value: "bis 40%", label: "Ersparnis möglich" },
  { value: "täglich", label: "Preis-Updates" },
];

const categoryDescriptions: Record<(typeof categories)[number]["id"], string> =
  {
    elektronik:
      "TVs, Kopfhörer, E-Scooter und Kameras — von führenden Marken zum besten Preis.",
    handys:
      "Smartphones, Smartwatches und Tablets im direkten Vergleich mehrerer Händler.",
    computer:
      "Laptops, Gaming-PCs und Zubehör — Konfigurationen und Preise auf einen Blick.",
  };

function HomePage() {
  return (
    <main className="relative">
      <SiteNav />

      <Hero
        eyebrow="Preisvergleich · Elektronik · Handys · Computer"
        title={
          <>
            Der beste Preis.
            <br />
            <span className="text-primary">Schwarz auf weiß.</span>
          </>
        }
        subtitle="Wir vergleichen tausende Angebote von Händlern — damit du nie mehr zu viel bezahlst."
        actions={
          <>
            <Link
              to="/preisvergleich"
              className="rounded-full border-2 border-foreground bg-foreground px-7 py-3.5 text-sm font-semibold text-background transition-colors hover:bg-primary hover:border-primary"
            >
              Preise vergleichen →
            </Link>
            <Link
              to="/kontakt"
              className="rounded-full border-2 border-foreground/25 px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:border-foreground"
            >
              Frag uns
            </Link>
          </>
        }
        decoration={<HeroProductChips />}
      />

      {/* Stats — printed as scattered receipt stubs */}
      <section className="relative py-24 px-6">
        <div className="mx-auto max-w-7xl grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 100}>
              <div
                className="h-full rounded-xl border border-foreground/12 bg-card p-6 md:p-7 ticket-shadow"
                style={{
                  transform: `rotate(${i % 2 === 0 ? "-1.1deg" : "1.1deg"})`,
                }}
              >
                <div className="font-mono text-4xl md:text-5xl font-bold tabular-nums text-foreground">
                  {s.value}
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {s.label}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Categories — three shelves */}
      <section className="relative py-24 px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="mb-16 max-w-2xl">
              <div className="text-xs font-mono uppercase tracking-[0.22em] text-primary mb-4">
                Kategorien
              </div>
              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-foreground">
                Drei Regale, ein Ziel.
              </h2>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {categories.map((c, i) => {
              const Art = productArt[c.art];
              return (
                <Reveal key={c.id} delay={i * 120}>
                  <Link
                    to="/preisvergleich"
                    search={{ category: c.id }}
                    className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-foreground/12 bg-card p-8 ticket-shadow transition-transform duration-300 hover:-translate-y-1"
                  >
                    <span className="punch-hole" />
                    <div className="flex justify-end text-foreground/15 group-hover:text-primary/25 transition-colors">
                      <Art className="h-24 w-24" tone="tag" />
                    </div>
                    <div className="mt-8">
                      <h3 className="text-2xl font-display font-semibold mb-2 text-foreground">
                        {c.label}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed text-sm">
                        {categoryDescriptions[c.id]}
                      </p>
                      <div className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-primary">
                        Ansehen <span aria-hidden>→</span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Chat */}
      <section className="relative py-24 px-6">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="mb-10 max-w-2xl">
              <div className="text-xs font-mono uppercase tracking-[0.22em] text-primary mb-4">
                Assistent
              </div>
              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-foreground">
                Frag einfach nach.
              </h2>
              <p className="mt-5 text-lg text-muted-foreground">
                Unser Assistent hilft dir, das passende Produkt und den besten
                Preis zu finden.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <HomeChat />
          </Reveal>
        </div>
      </section>

      {/* CTA — inverted ink band */}
      <section className="relative py-28 px-6 bg-foreground text-background overflow-hidden">
        <div className="pointer-events-none absolute inset-0 grid-bg text-background/10" />
        <Reveal>
          <div className="relative mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-6xl font-bold leading-tight">
              Bereit für den <span className="text-primary">besten Preis</span>?
            </h2>
            <p className="mt-6 text-lg text-background/70 max-w-xl mx-auto">
              Starte jetzt deinen ersten Vergleich — kostenlos und ohne
              Anmeldung.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <Link
                to="/preisvergleich"
                className="rounded-full border-2 border-primary bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
              >
                Preise vergleichen
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <SiteFooter />
    </main>
  );
}
